import React, { useState, useRef } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

/**
 * ImageCropModal
 * Props:
 *   src        – object URL of the selected image file
 *   uploading  – boolean, true while the cropped blob is being uploaded
 *   onConfirm  – called with (croppedBlob) when user clicks "Crop & Use"
 *   onSkip     – called with no args; user wants to upload the original without cropping
 *   onCancel   – called when user clicks Cancel
 */
export default function ImageCropModal({ src, uploading, onConfirm, onSkip, onCancel }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();

  // Set a sensible default crop when the image first loads
  const onImageLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    const defaultCrop = centerCrop(
      makeAspectCrop({ unit: "%", width: 85 }, undefined, w, h),
      w, h
    );
    setCrop(defaultCrop);
    setCompletedCrop(defaultCrop);
  };

  // Use canvas to extract the cropped region as a Blob
  const handleCropAndUse = () => {
    if (!completedCrop || !imgRef.current) return;
    const img = imgRef.current;
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;

    const canvas = document.createElement("canvas");
    canvas.width  = Math.round(completedCrop.width  * scaleX);
    canvas.height = Math.round(completedCrop.height * scaleY);

    if (canvas.width === 0 || canvas.height === 0) {
      console.error("Crop area is zero size — falling back to full image");
      onSkip();
      return;
    }

    const ctx = canvas.getContext("2d");

    // Make sure canvas is not tainted by CORS by wrapping in try/catch
    try {
      ctx.drawImage(
        img,
        Math.round(completedCrop.x      * scaleX),
        Math.round(completedCrop.y      * scaleY),
        Math.round(completedCrop.width  * scaleX),
        Math.round(completedCrop.height * scaleY),
        0, 0, canvas.width, canvas.height
      );
    } catch (err) {
      console.error("Canvas drawImage failed (likely CORS):", err);
      // Fall back: upload the original file without cropping
      onSkip();
      return;
    }

    // Try JPEG first, then PNG as fallback
    canvas.toBlob(blob => {
      if (blob) {
        console.log("Crop blob created, size:", blob.size, "bytes");
        onConfirm(blob);
      } else {
        console.warn("canvas.toBlob returned null for JPEG — retrying as PNG");
        canvas.toBlob(pngBlob => {
          if (pngBlob) {
            console.log("PNG blob created, size:", pngBlob.size, "bytes");
            onConfirm(pngBlob);
          } else {
            console.error("canvas.toBlob failed for both JPEG and PNG — falling back to full image");
            alert("Could not extract the cropped region. Uploading the full image instead.");
            onSkip();
          }
        }, "image/png");
      }
    }, "image/jpeg", 0.92);
  };

  return (
    /* ── Backdrop ── */
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
      zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px", boxSizing: "border-box"
    }}>
      {/* ── Modal ── */}
      <div style={{
        background: "#fff", borderRadius: 16, padding: "24px",
        maxWidth: 780, width: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
        display: "flex", flexDirection: "column", gap: 16, maxHeight: "90vh"
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ margin: 0, color: "#1a237e", fontSize: "1.1rem" }}>✂️ Crop Image</h3>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: "0.82rem" }}>
              Drag the handles to select the region you want to use. The rest will be removed.
            </p>
          </div>
          <button onClick={onCancel} style={{ background: "none", border: "none", fontSize: "1.4rem", cursor: "pointer", color: "#888", lineHeight: 1 }}>✕</button>
        </div>

        {/* Crop area */}
        <div style={{ overflowY: "auto", maxHeight: "55vh", display: "flex", justifyContent: "center", background: "#f0f0f0", borderRadius: 10, padding: 8 }}>
          <ReactCrop
            crop={crop}
            onChange={c => setCrop(c)}
            onComplete={c => setCompletedCrop(c)}
            minWidth={20}
            minHeight={20}
          >
            <img
              ref={imgRef}
              src={src}
              onLoad={onImageLoad}
              alt="crop-source"
              crossOrigin="anonymous"
              style={{ maxWidth: "100%", maxHeight: "50vh", display: "block", objectFit: "contain" }}
            />
          </ReactCrop>
        </div>

        {/* Tips */}
        <div style={{ background: "#e8f5e9", borderRadius: 8, padding: "8px 14px", fontSize: "0.80rem", color: "#2e7d32" }}>
          💡 <strong>Tip:</strong> Take a photo of your textbook page, then drag to select just the diagram or equation you need.
          The crop preserves full original resolution.
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button onClick={onCancel} disabled={uploading}
            style={{ background: "#fff", color: "#555", border: "1.5px solid #ddd", borderRadius: 10, padding: "9px 20px", fontWeight: 600, cursor: "pointer", fontSize: "0.9rem" }}>
            Cancel
          </button>
          <button onClick={onSkip} disabled={uploading}
            style={{ background: "#fff3e0", color: "#e65100", border: "1.5px solid #ffcc80", borderRadius: 10, padding: "9px 20px", fontWeight: 600, cursor: "pointer", fontSize: "0.9rem" }}>
            📤 Use Full Image
          </button>
          <button onClick={handleCropAndUse} disabled={uploading || !completedCrop}
            style={{ background: uploading ? "#ccc" : "linear-gradient(135deg,#1a237e,#1565c0)", color: "#fff", border: "none", borderRadius: 10, padding: "9px 24px", fontWeight: 700, cursor: uploading ? "not-allowed" : "pointer", fontSize: "0.9rem" }}>
            {uploading ? "⏳ Uploading…" : "✂️ Crop & Use"}
          </button>
        </div>
      </div>
    </div>
  );
}
